import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { locales } from "@/lib/i18n/config";
import { useLogoutMutation } from "@/shared/services/useLogoutMutation";
import { deregisterFcmTokenFromBackend } from "@/lib/firebase/fcm";
import { ThemeToggle } from "./ThemeToggle";
import type { NavItem } from "./nav";

type BottomNavIdentity = {
  name: string;
  initial: string;
  /** Profile page — the identity header links here. */
  href: string;
};

type BottomNavProps = {
  /** All destinations in display order. The collapsed rail shows the first 3. */
  tabs: NavItem[];
  identity: BottomNavIdentity;
  locale: string;
};

/**
 * Mobile bottom navigation — the seller portal's floating morphing pill
 * applied to the admin portal. Collapsed it's a rail of the first 3
 * destinations + a "More" toggle; tapping More grows the pill UPWARD into a
 * full menu — headed by the admin identity, with the route-less actions
 * (theme, language, logout) pinned at the bottom. The admin portal has no
 * global search backend, so there is no companion search button.
 */
export function BottomNav({ tabs, identity, locale }: BottomNavProps) {
  const t = useTranslations();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const logoutMutation = useLogoutMutation();

  // Close the menu on a tap anywhere outside the bar.
  useEffect(() => {
    if (!expanded) return;
    const onDown = (e: PointerEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) {
        setExpanded(false);
      }
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [expanded]);

  const railTabs = tabs.slice(0, 3);
  // The "More" toggle lights up when the active route isn't one of the rail 3.
  const moreActive = !railTabs.some((tab) => tab.isActive);

  const otherLocale = locale === "ar" ? "en" : "ar";
  const otherLocaleLabel =
    otherLocale === "ar"
      ? t(keys.common.languageArabic)
      : t(keys.common.languageEnglish);

  const handleSwitchLocale = () => {
    setExpanded(false);
    const path = pathname ?? "/";
    const localePattern = new RegExp(`^/(${locales.join("|")})(/|$)`);
    const nextPath = localePattern.test(path)
      ? path.replace(localePattern, `/${otherLocale}$2`)
      : `/${otherLocale}${path.startsWith("/") ? "" : "/"}${path}`;
    navigate(nextPath, { replace: true });
  };

  const handleLogout = async () => {
    setExpanded(false);
    // Deregister FCM token before logging out (best-effort)
    await deregisterFcmTokenFromBackend().catch(() => {});
    await logoutMutation.mutateAsync();
    navigate(`/${locale}/admin/login`, { replace: true });
  };

  const railBtn =
    "relative flex h-12 w-14 items-center justify-center rounded-full transition-colors";
  // Pinned action rows (language, logout) — same rhythm as the destination
  // links above them, but rendered as <button> since they have no route.
  const menuActionClass =
    "flex w-full items-center gap-3 rounded-[14px] px-3 py-2.5 text-start text-[13.5px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--soft)]";

  return (
    <nav
      aria-label="Primary"
      className="pointer-events-none fixed inset-x-0 z-40 flex justify-center px-3 lg:hidden"
      style={{ bottom: "calc(14px + env(safe-area-inset-bottom, 0px))" }}
    >
      <div ref={barRef} className="flex w-full max-w-sm flex-col items-stretch">
        <div className="pointer-events-auto min-w-0 overflow-hidden rounded-[26px] border border-[color:var(--card-br)] bg-[color:var(--card)] shadow-[0_12px_36px_rgba(0,0,0,0.16)] sm:mx-auto sm:w-full sm:max-w-[280px]">
          {/* Expand menu — identity header, every destination, then the pinned
              route-less actions. A flex column so the destinations list
              (flex-1) absorbs the slack and scrolls, while the header and the
              pinned footer stay put and never get clipped off the viewport. */}
          <div
            className={[
              "flex flex-col overflow-hidden transition-all duration-300 ease-out",
              expanded ? "max-h-[75vh] opacity-100" : "max-h-0 opacity-0",
            ].join(" ")}
          >
            {/* Header: admin identity → profile. */}
            <div className="flex shrink-0 items-center gap-2.5 px-3 pb-1.5 pt-3">
              <Link
                to={identity.href}
                onClick={() => setExpanded(false)}
                className="flex min-w-0 flex-1 items-center gap-2.5 rounded-[10px] transition-colors hover:bg-[color:var(--soft)]"
              >
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] text-[13px] font-bold"
                  style={{
                    background: "var(--accent-500)",
                    color: "var(--on-accent)",
                  }}
                >
                  {identity.initial}
                </span>
                <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-[color:var(--text-primary)]">
                  {identity.name}
                </span>
              </Link>
            </div>
            <div className="mx-3 my-1 h-px shrink-0 bg-[color:var(--hair)]" />

            <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overscroll-contain p-2 pt-1">
              {tabs.map((tab) => (
                <Link
                  key={tab.key}
                  to={tab.href}
                  onClick={() => setExpanded(false)}
                  aria-current={tab.isActive ? "page" : undefined}
                  className={[
                    "flex items-center gap-3 rounded-[14px] px-3 py-2.5 text-[13.5px] transition-colors",
                    tab.isActive
                      ? "font-semibold text-[color:var(--accent-500)]"
                      : "font-medium text-[color:var(--text-primary)] hover:bg-[color:var(--soft)]",
                  ].join(" ")}
                  style={
                    tab.isActive
                      ? {
                          background:
                            "color-mix(in srgb, var(--accent-500) 10%, transparent)",
                        }
                      : undefined
                  }
                >
                  <span className="flex shrink-0">{tab.icon}</span>
                  <span className="truncate">{tab.label}</span>
                </Link>
              ))}
            </div>

            {/* Route-less actions — pinned at the bottom of the menu. Same
                row rhythm as the destinations above (icon + label). */}
            <div className="mx-3 h-px shrink-0 bg-[color:var(--hair)]" />
            <div className="flex shrink-0 flex-col gap-0.5 p-2">
              <div className="px-1 py-1">
                <ThemeToggle />
              </div>
              <button
                type="button"
                onClick={handleSwitchLocale}
                className={menuActionClass}
              >
                <span className="flex shrink-0">
                  <GlobeIcon />
                </span>
                <span className="truncate">{otherLocaleLabel}</span>
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className={menuActionClass}
              >
                <span className="flex shrink-0">
                  <LogoutIcon />
                </span>
                <span className="truncate">{t(keys.common.logout)}</span>
              </button>
            </div>
          </div>

          {/* Rail — first 3 icons + More toggle. Collapses for the menu. */}
          <div
            className={[
              "overflow-hidden transition-all duration-300 ease-out",
              expanded ? "max-h-0 opacity-0" : "max-h-16 opacity-100",
            ].join(" ")}
          >
            <div className="flex items-center justify-between p-1.5">
              {railTabs.map((tab) => (
                <Link
                  key={tab.key}
                  to={tab.href}
                  aria-label={tab.label}
                  aria-current={tab.isActive ? "page" : undefined}
                  className={railBtn}
                  style={{
                    background: tab.isActive ? "var(--soft2)" : "transparent",
                    color: tab.isActive
                      ? "var(--text-primary)"
                      : "var(--text-muted)",
                  }}
                >
                  {tab.icon}
                </Link>
              ))}
              <button
                type="button"
                aria-label={t(keys.admin.navMore)}
                aria-expanded={expanded}
                onClick={() => setExpanded(true)}
                className={railBtn}
                style={{
                  background: moreActive ? "var(--soft2)" : "transparent",
                  color: moreActive ? "var(--text-primary)" : "var(--text-muted)",
                }}
              >
                <ChevronsUpDownIcon />
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const NAV_ICON = {
  width: 22,
  height: 22,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

/** Rail "More" toggle glyph (two stacked chevrons). */
function ChevronsUpDownIcon() {
  return (
    <svg {...NAV_ICON}>
      <path d="m7 15 5 5 5-5" />
      <path d="m7 9 5-5 5 5" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg {...NAV_ICON}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a15 15 0 0 1 4 9 15 15 0 0 1-4 9 15 15 0 0 1-4-9 15 15 0 0 1 4-9Z" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg {...NAV_ICON}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}
